import React, { useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { Modal, Form, Input, Button, Progress } from 'antd'
import { useTranslation } from 'react-i18next'
import { getTempEnvParam, setTempEnvParam, removeTempEnvParam } from 'env'
import { getMyInfo } from 'services/landing/utils/api'
import { syncRecoveryPubKeyFromAuthData } from 'pUtils'
import { passwordStrength } from 'utils/ui'
import styles from '../style.module.scss'

// 인라인 SVG로 렌더 — <img src> 를 토글하면 처음엔 다른 svg 파일을 새로 받느라
// 아이콘이 늦게 바뀐다. 인라인이면 파일 로드 없이 즉시 전환된다.
const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.0003 12C11.1048 12 12.0003 11.1046 12.0003 10C12.0003 8.89543 11.1048 8 10.0003 8C8.89568 8 8.00025 8.89543 8.00025 10C8.00025 11.1046 8.89568 12 10.0003 12Z"
      fill="#6B7280"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.458008 10C1.73226 5.94291 5.52256 3 10.0002 3C14.4778 3 18.2681 5.94288 19.5424 9.99996C18.2682 14.0571 14.4778 17 10.0002 17C5.52257 17 1.73228 14.0571 0.458008 10ZM14.0003 10C14.0003 12.2091 12.2094 14 10.0003 14C7.79111 14 6.00025 12.2091 6.00025 10C6.00025 7.79086 7.79111 6 10.0003 6C12.2094 6 14.0003 7.79086 14.0003 10Z"
      fill="#6B7280"
    />
  </svg>
)

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.70711 2.29289C3.31658 1.90237 2.68342 1.90237 2.29289 2.29289C1.90237 2.68342 1.90237 3.31658 2.29289 3.70711L16.2929 17.7071C16.6834 18.0976 17.3166 18.0976 17.7071 17.7071C18.0976 17.3166 18.0976 16.6834 17.7071 16.2929L16.2339 14.8197C17.7715 13.5924 18.939 11.9211 19.5424 9.99996C18.2681 5.94288 14.4778 3 10.0002 3C8.37665 3 6.84344 3.38692 5.48779 4.07358L3.70711 2.29289ZM7.96813 6.55391L9.48201 8.0678C9.6473 8.02358 9.82102 8 10.0003 8C11.1048 8 12.0003 8.89543 12.0003 10C12.0003 10.1792 11.9767 10.353 11.9325 10.5182L13.4463 12.0321C13.7983 11.4366 14.0003 10.7419 14.0003 10C14.0003 7.79086 12.2094 6 10.0003 6C9.25838 6 8.56367 6.20197 7.96813 6.55391Z"
      fill="#6B7280"
    />
    <path
      d="M12.4541 16.6967L9.74965 13.9923C7.74013 13.8681 6.1322 12.2601 6.00798 10.2506L2.33492 6.57754C1.50063 7.57223 0.856368 8.73169 0.458008 10C1.73228 14.0571 5.52257 17 10.0002 17C10.8469 17 11.6689 16.8948 12.4541 16.6967Z"
      fill="#6B7280"
    />
  </svg>
)

const eyeRender = (visible) => (
  <span
    role="img"
    aria-label={visible ? 'Show password' : 'Hide password'}
    style={{ display: 'inline-flex', cursor: 'pointer' }}
  >
    {visible ? <EyeOffIcon /> : <EyeIcon />}
  </span>
)

const ExclamationCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 5.333V8m0 2.667h.007M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0Z"
      stroke="#1F2937"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ChangeMasterPasswordModal = (props) => {
  const { open, onClose, messageApi } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const formRef = useRef(null)
  const [strength, setStrength] = useState(0)
  const [hasNewPassword, setHasNewPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingKey, setIsLoadingKey] = useState(false)
  const [keyLoadError, setKeyLoadError] = useState(false)

  // 활성(auth_type=login) 세션은 auth_info가 user_key를 주지 않아 base64privateKey temp env가 비어,
  // changePW가 0바이트 IV로 크래시한다(forge AES-CBC). /users/me(include_sensitive)로 enc_priv를 선로딩.
  // (changeMasterPW 페이지 패턴 복제 — pages/auth/changeMasterPW/index.js)
  //
  // 828 수정 v3 (me_recovery_pubkey_for_changepw_828.md): tempEnv 는 in-memory 라 리로드 시 소실되고,
  // 다른 디바이스에서 비밀번호/복구키가 바뀌면 남아있는 캐시도 stale 이 된다. 그래서 캐시 유무와
  // 무관하게 매 open 시 /me 로 enc_priv + recovery_pub_key 를 같은 스냅샷으로 받아 덮어쓴다
  // (조기종료 제거 — 캐시는 성능용이 아니라 changePW 전달 통로).
  useEffect(() => {
    if (!open) {
      return undefined
    }
    let cancelled = false
    setIsLoadingKey(true)
    setKeyLoadError(false)
    getMyInfo()
      .then((res) => {
        if (cancelled) {
          return
        }
        const userKey = res?.data?.user_key
        if (userKey) {
          // 항상 덮어쓰기 — cross-device 변경 후 stale enc_priv 로 인한 복호화 실패 차단
          setTempEnvParam('base64privateKey', userKey)
        } else if (!getTempEnvParam('base64privateKey')) {
          // 응답 성공했으나 user_key 누락 + 캐시도 없음 — changePW 크래시 방지 위해 제출 차단
          console.warn('ChangeMasterPasswordModal: user_key missing in /me response')
          setKeyLoadError(true)
          messageApi?.error(t('common.recovery.loadKeyFailed'))
        }
        // 복구 공개키 동기화 — base64recoveryPublicKey + recoveryPubKeyOwner(=res.data.user_email)를
        // 함께 set 해 changePW owner-check 통과. NONE 사용자는 null → sync 내부에서 캐시 remove.
        // 응답이 비정상(data 부재/recovery_pub_key 키 자체 없음)이면 sync 가 'in' 가드로 no-op 이라
        // stale 캐시가 살아남으므로, catch(F3=A)와 동일하게 명시 remove (gemini 리뷰 #1450).
        const resData = res?.data
        if (resData && typeof resData === 'object' && 'recovery_pub_key' in resData) {
          syncRecoveryPubKeyFromAuthData(resData)
        } else {
          removeTempEnvParam('base64recoveryPublicKey')
          removeTempEnvParam('recoveryPubKeyOwner')
        }
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        console.error('ChangeMasterPasswordModal: failed to load user_key', err)
        // review-plan F3=A: 서버 최신값을 확인 못 한 상태의 복구공개키 캐시는 stale 일 수 있고
        // (타 디바이스 복구키 재등록), 그 값으로 재봉인하면 enc_umk↔rec_public_key 가 영구
        // desync 된다(오염). 캐시를 제거해 new_enc_umk 미생성 → 서버 828 명시 거부(일시적,
        // 모달 재시도로 회복)로 유도 — 오염 원천 차단이 변경 성공률보다 우선.
        removeTempEnvParam('base64recoveryPublicKey')
        removeTempEnvParam('recoveryPubKeyOwner')
        // enc_priv 는 성격이 다름: stale 이어도 복호화 실패로 즉시 드러나(서버 오염 없음),
        // 캐시 부재 시에만 제출 차단(over-block 방지 — 캐시 보유 사용자는 그 값으로 진행).
        if (!getTempEnvParam('base64privateKey')) {
          setKeyLoadError(true)
          messageApi?.error(t('common.recovery.loadKeyFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingKey(false)
        }
      })
    return () => {
      cancelled = true
    }
    // open 진입 시 1회 키 선로딩 — messageApi/t 는 effect 내부 참조(최신 closure)로 충분, deps 제외해 재실행 루프 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handlePasswordChange = () => {
    const value = formRef.current?.getFieldValue('newPassword') || ''
    setHasNewPassword(value.length > 0)
    setStrength(passwordStrength({ value }).calculate())
  }

  const handleClose = () => {
    formRef.current?.resetFields()
    setStrength(0)
    setHasNewPassword(false)
    setIsSubmitting(false)
    setIsLoadingKey(false)
    setKeyLoadError(false)
    onClose()
  }

  // saga UMK_CHANGEPW가 성공/실패 모두 호출하는 단일 콜백. 성공이면 닫고, 실패면 제출 상태만 해제
  // (에러 팝업은 umkChangePW가 errorBase로 이미 표시 — 이중 팝업 방지).
  const handleComplete = (success) => {
    if (success) {
      messageApi?.success(t('appConfig.settings.successChangeMasterPassword'))
      handleClose()
    } else {
      setIsSubmitting(false)
    }
  }

  const handleChange = async () => {
    // Enter 제출 경로는 버튼 disabled 를 우회하므로, 버튼과 동일 조건으로 차단 (중복 dispatch 방지)
    if (isSubmitting || isLoadingKey || keyLoadError) {
      return
    }
    let values
    try {
      values = await formRef.current?.validateFields()
    } catch (e) {
      // 폼 검증 실패 — antd가 필드별 에러 표시, 제출 중단
      return
    }
    if (!getTempEnvParam('base64privateKey')) {
      // 키 미로딩 상태(로딩 중/실패)/빈 키(falsy) 제출 차단 — 빈 키 크래시 방지
      messageApi?.error(t('common.recovery.loadKeyFailed'))
      return
    }
    setIsSubmitting(true)
    // 활성 사용자 자발적 변경 — wasTemp:false 필수(true면 ESCROW 복구 붕괴). fromSettings로 nav/reload skip.
    dispatch({
      type: 'user/UMK_CHANGEPW',
      payload: {
        password: values.original,
        newPassword: values.newPassword,
        wasTemp: false,
        fromSettings: true,
        onComplete: handleComplete,
      },
    })
  }

  const confirmRules = [
    { required: true, message: t('email.desc.requiredPWConfirm') },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (!value || getFieldValue('newPassword') === value) {
          return Promise.resolve()
        }
        return Promise.reject(new Error(t('email.desc.pwNotMatch')))
      },
    }),
  ]

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      width={536}
      destroyOnClose
      wrapClassName={classNames(styles.changeMPWModal)}
    >
      <div className={classNames(styles.mpwContent)}>
        <p className={classNames(styles.mpwTitle)}>
          {t('appConfig.settings.changeMasterPassword')}
        </p>

        <div className={classNames(styles.mpwInfo)}>
          <ExclamationCircleIcon />
          <span>{t('login.desc.masterPW')}</span>
        </div>

        {/* 디바이스 폐기 사전 안내 — MPW 변경 시 UMK 변경으로 모든 기기의 패스키 자동 잠금해제가 폐기됨 */}
        <div className={classNames(styles.mpwInfo)}>
          <ExclamationCircleIcon />
          <span>{t('appConfig.settings.changeMasterPasswordDeviceNotice')}</span>
        </div>

        <Form
          ref={formRef}
          layout="vertical"
          className={classNames(styles.mpwForm)}
          validateTrigger={[]}
        >
          <Form.Item
            name="original"
            label={t('appConfig.settings.originalMasterPassword')}
            rules={[{ required: true, message: t('login.desc.requiredPW') }]}
          >
            <Input.Password
              placeholder={t('appConfig.settings.originalMasterPasswordPlaceholder')}
              autoComplete="current-password"
              iconRender={eyeRender}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('login.title.masterPW')}
            rules={[
              { required: true, message: t('login.desc.requiredPW') },
              { min: 12, message: t('login.desc.masterPW') },
            ]}
            extra={
              hasNewPassword ? (
                <div className={classNames(styles.mpwStrength)}>
                  <Progress
                    showInfo={false}
                    percent={passwordStrength({ strength }).labels}
                    strokeColor={passwordStrength({ strength }).color}
                  />
                  <span>{passwordStrength({ strength }).text}</span>
                </div>
              ) : null
            }
          >
            <Input.Password
              placeholder={t('login.placeholder.newMasterPW')}
              autoComplete="new-password"
              onChange={handlePasswordChange}
              iconRender={eyeRender}
            />
          </Form.Item>

          <Form.Item name="confirm" label={t('login.title.confirmPW')} rules={confirmRules}>
            <Input.Password
              placeholder={t('login.placeholder.confirmNewMasterPW')}
              autoComplete="new-password"
              iconRender={eyeRender}
              onPressEnter={handleChange}
            />
          </Form.Item>
        </Form>
      </div>

      <div className={classNames(styles.mpwFooter)}>
        <Button
          className={classNames('h38', styles.mpwCancelBtn)}
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {t('common.button.cancel')}
        </Button>
        <Button
          className={classNames('h38', styles.mpwChangeBtn)}
          onClick={handleChange}
          loading={isSubmitting || isLoadingKey}
          disabled={isSubmitting || isLoadingKey || keyLoadError}
        >
          {t('common.button.change')}
        </Button>
      </div>
    </Modal>
  )
}

export default ChangeMasterPasswordModal
